import ArchiveIcon from "@mui/icons-material/Inventory2Outlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import UnarchiveIcon from "@mui/icons-material/UnarchiveOutlined";
import { Box, Paper, Typography } from "@mui/material";
import {
  DataGridPremium as DataGridPro,
  GridActionsCellItem,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  Toolbar,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { Dispatch } from "@reduxjs/toolkit";
import { ConfirmOptions, useConfirm } from "material-ui-confirm";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch, useSelector } from "react-redux";
import {
  defaultDataGridProps,
  injectDefaultColumnProps,
} from "../../constants";
import { JobCategory_Entity } from "../../entities/jobCategory";
import { TaskType_Entity } from "../../entities/taskType";

import { useJobCategories } from "../../redux/jobCategory/jobCategoryHooks";
import {
  useCreateTaskTypeMutation,
  useDeleteTaskTypeMutation,
  useUpdateTaskTypeMutation,
} from "../../redux/taskType/taskTypeEndpoints";
import { useTaskTypes } from "../../redux/taskType/taskTypeHooks";
import NoRowsOverlay from "../noRowsOverlay/noRowsOverlay";
import { Button, Checkbox } from "@mui/joy";
import {
  setFormOpen,
  setSelectedTaskTypeID,
  setShowArchivedRates,
  showArchivedRates,
} from "../../redux/filmsReducer";

/**
 * TaskType
 *
 * @returns {null} TaskType
 */
export default function TaskTypes() {
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const confirm = useConfirm();
  const { jobCategories } = useJobCategories();
  const showArchived = useSelector(showArchivedRates);
  const { taskTypes: _taskTypes } = useTaskTypes();
  const taskTypes = _taskTypes?.filter((tt) => showArchived || !tt.archived);
  const [deleteTaskType] = useDeleteTaskTypeMutation();
  const [updateTaskType] = useUpdateTaskTypeMutation();

  const onTaskTypeUpdate = async (
    _newTaskType: TaskType_Entity,
    _oldTaskType: TaskType_Entity
  ) => {
    // Wait for the server rather than resolving on a 50ms timer: the grid must
    // not leave edit mode before the write lands, or the refetch that follows
    // reverts the cell to its pre-edit value.
    await updateTaskType({
      id: _newTaskType.id,
      body: { ..._newTaskType },
    }).unwrap();
    return { ..._newTaskType };
  };

  return (
    <Paper
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
        maxHeight: "calc(100vh - 78px)",
      }}
    >
      <DataGridPro
        {...defaultDataGridProps}
        apiRef={apiRef}
        onRowClick={(e) => {
          dispatch(setSelectedTaskTypeID(e.row.id));
          dispatch(setFormOpen({ isOpen: true, formID: "taskType" }));
        }}
        rows={taskTypes ?? []}
        getRowClassName={(p) => (p.row.archived ? "archived" : "")}
        columns={taskTypeColumns(
          dispatch,
          confirm,
          jobCategories ?? [],
          deleteTaskType,
          updateTaskType
        )}
        showToolbar
        slots={{
          toolbar: TaskTypeToolbar,
          noRowsOverlay: NoRowsOverlay,
        }}
      />
    </Paper>
  );
}

function TaskTypeToolbar() {
  const dispatch = useDispatch();
  const showArchived = useSelector(showArchivedRates);

  return (
    <Toolbar>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          p: 1,
          gap: 2,
          alignItems: "center",
          width: "100%",
        }}
      >
        <Button
          variant="soft"
          size="sm"
          onClick={() =>
            dispatch(setFormOpen({ formID: "taskType", isOpen: true }))
          }
        >
          + New Task Type
        </Button>
        <QuickFilter
          expanded
          style={{ flex: 1, display: "flex", alignItems: "center" }}
        >
          <QuickFilterControl size="small" fullWidth placeholder="Search..." />
          <QuickFilterClear />
        </QuickFilter>
        <Checkbox
          size="sm"
          variant="outlined"
          label="Show Archived"
          onChange={(e) => dispatch(setShowArchivedRates(e.target.checked))}
          checked={showArchived}
        />
      </Box>
    </Toolbar>
  );
}

const taskTypeColumns = (
  dispatch: Dispatch,
  confirm: (options?: ConfirmOptions | undefined) => Promise<void>,
  jobCategories: JobCategory_Entity[],
  deleteTaskType: (id: number) => void,
  updateTaskType: (args: { id: number; body: Partial<TaskType_Entity> }) => void
) =>
  injectDefaultColumnProps([
    {
      field: "id",
      headerName: "ID",
      editable: false,
      type: "number",
      width: 40,
    },
    {
      field: "name",
      headerName: "Name",
      editable: true,
      type: "string",
      flex: 1,
    },
    {
      field: "jobCategoryID",
      headerName: "Job Category",
      editable: true,
      flex: 1,
      type: "singleSelect",
      valueOptions: getOptions(jobCategories),
      renderCell: (p: any) => {
        const jobCategory = jobCategories.find(
          (s) => s.id === p.row.jobCategoryID
        );
        return <Typography variant="body2">{jobCategory?.name}</Typography>;
      },
    },
    {
      field: "unit",
      headerName: "Unit",
      editable: true,
      flex: 1,
      type: "singleSelect",
      valueOptions: [
        { value: "page", label: "Page" },
        { value: "hour", label: "Hour" },
      ],
    },
    {
      field: "pos",
      headerName: "Order",
      editable: true,
      width: 90,
      type: "number",
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: (props: { id: any; row: TaskType_Entity }) => [
        <GridActionsCellItem
          key="archive"
          showInMenu
          icon={props.row.archived ? <UnarchiveIcon /> : <ArchiveIcon />}
          label={props.row.archived ? "Unarchive" : "Archive"}
          onClick={() => {
            if (props.row.archived) {
              updateTaskType({ id: props.id, body: { archived: false } });
              return;
            }
            confirm({
              description: `${props.row.name} will drop out of the rate grid. Tasks already recorded against it keep their rates, and "Show Archived" brings it back.`,
            })
              .then(() => {
                updateTaskType({ id: props.id, body: { archived: true } });
              })
              .catch(() => {
                /* ... */
              });
          }}
          color="inherit"
        />,
        <GridActionsCellItem
          key="delete"
          showInMenu
          icon={<DeleteIcon />}
          label={"Delete"}
          onClick={() => {
            confirm({
              description: `You are about to delete a taskType`,
            })
              .then(() => {
                deleteTaskType(props.id);
              })
              .catch(() => {
                /* ... */
              });
          }}
          color="inherit"
        />,
      ],
    },
  ]);

function getOptions(items: { id: number; name?: string }[]) {
  const res = [];
  for (const key in items) {
    if (Object.prototype.hasOwnProperty.call(items, key)) {
      const item = items[key];
      res.push({
        value: item.id,
        label: `${item.id}: ${item.name}`,
      });
    }
  }

  return res;
}
